// VehicleForm.jsx
import {
  React,
  useEffect,
  useState,
  useClientInfo,
  axiosInstance,
  tableStyles,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Typography,
  Menu,
  TablePagination,
  Snackbar,
  Alert,
  MoreVertIcon,
  AddIcon,
  DeleteIcon,
  EditIcon,
  VehicleFormAdd,
  CategoryDropdown,
} from "../../utils/commonImports";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const FILTERS = [
  "All Vehicles",
  "Active",
  "Inactive",
  "Own Vehicles",
  "Attach Vehicles",
  "Vendor Vehicles",
  "Vehicle Type",
  "Vehicle Group",
];

const API = "/api/vehicles";
// const CLIENT_ID = 1; // TODO: replace with auth/context based client id

export default function VehicleForm() {
  const { clientId } = useClientInfo(); // ✅ dynamic clientId
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [filter, setFilter] = useState(FILTERS[0]);
  const [categoryFilterId, setCategoryFilterId] = useState(""); // For Vehicle Type or Group filter
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleTypeMap, setVehicleTypeMap] = useState({});
  const [filterOptions, setFilterOptions] = useState([]);

  const categoryTypeMap = {
    "Vehicle Type": "VehicleType",
    "Vehicle Group": "VehicleGroup",
  };

  // useEffect(() => {
  //   axiosInstance
  //     .get("/api/components/category?type=Vehicletype")
  //     .then((res) => {
  //       const types = res.data || [];
  //       setVehicleTypes(types);
  //       const map = {};
  //       types.forEach((v) => {
  //         map[String(v.categoryid)] = v.categoryname;
  //       });
  //       setVehicleTypeMap(map);
  //     })
  //     .catch(() => {
  //       setVehicleTypes([]);
  //       setVehicleTypeMap({});
  //     });
  // }, []);

  useEffect(() => {
    const categoryType = categoryTypeMap[filter];
    console.log("Selected filter:", filter);
    console.log("Mapped categoryType:", categoryType);

    if (categoryType) {
      axiosInstance
        .get(`/api/components/category?type=${categoryType}`)
        .then((res) => {
          console.log("API response:", res.data);
          setFilterOptions(res.data || []);
        })
        .catch((err) => {
          console.error("API error:", err);
          setFilterOptions([]);
        });
    } else {
      setFilterOptions([]);
    }
  }, [filter]);

  // Fetch vehicle list
  const fetchRows = async () => {
    try {
      // Build query params for filter
      let params = { client_id: clientId };
      switch (filter) {
        case "Active":
          params.active = "Yes";
          break;
        case "Inactive":
          params.active = "No";
          break;
        case "Own Vehicles":
        case "Attach Vehicles":
        case "Vendor Vehicles":
          params.vehicle_ownership = filter.replace(" Vehicles", "");
          break;
        case "Vehicle Type":
          if (categoryFilterId) params.vehicle_type_id = categoryFilterId;
          break;
        case "Vehicle Group":
          if (categoryFilterId) params.vehicle_group_id = categoryFilterId;
          break;
        default:
          break;
      }
      const res = await axiosInstance.get(API, {
        params: { ...params, limit: rowsPerPage, offset: page * rowsPerPage },
      });
      console.log("Vehicle row data:", res.data); // <-- Add this line
      setRows(res.data);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch vehicles.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    fetchRows();
  }, [filter, categoryFilterId, page, rowsPerPage, clientId]);

  // Menu handlers (Edit/Delete)
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleDelete = async (row) => {
    handleRowMenuClose();
    if (!window.confirm("Are you sure you want to delete this vehicle?"))
      return;
    try {
      await axiosInstance.delete(`${API}/${row.vehicle_id}`, {
        params: { client_id: clientId },
      });

      setSnackbar({ open: true, msg: "Vehicle deleted.", severity: "success" });
      fetchRows();
    } catch (err) {
      setSnackbar({ open: true, msg: "Delete failed.", severity: "error" });
    }
  };

  const handleEdit = async (row) => {
    console.log("Editing row:", row);
    try {
      const res = await axiosInstance.get(`${API}/${row.vehicle_id}`, {
        params: { client_id: clientId },
      });

      setEditData(res.data);
      setShowForm(true);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch vehicle details.",
        severity: "error",
      });
    }
    handleRowMenuClose();
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        p: { xs: 0, md: 0 },
        bgcolor: "#f8fafc",
      }}
    >
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between", // space between groups: left group & Add button
            alignItems: "center",
            mb: 2,
          }}
        >
          {/* Left group: Filter + CategoryDropdown */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="vehicle-filter-label">Filter</InputLabel>
              <Select
                labelId="vehicle-filter-label"
                value={filter}
                label="Filter"
                onChange={(e) => {
                  setFilter(e.target.value);
                  setCategoryFilterId("");
                }}
                sx={{ fontSize: 13 }}
              >
                {FILTERS.map((f) => (
                  <MenuItem key={f} value={f} sx={{ fontSize: 13 }}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(filter === "Vehicle Type" || filter === "Vehicle Group") && (
              <Box
                sx={{
                  width: "20vw",
                  minHeight: "5vh",
                  margin: 0,
                  padding: 0,
                }}
              >
                <div className="small-font-dropdown">
                  {(filter === "Vehicle Type" ||
                    filter === "Vehicle Group") && (
                    <CategoryDropdown
                      categoryType={categoryTypeMap[filter]}
                      options={filterOptions}
                      value={categoryFilterId}
                      onChange={setCategoryFilterId}
                      size="small"
                      label={filter}
                    />
                  )}
                </div>
              </Box>
            )}
          </Box>

          {/* Right side: Add button */}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            sx={{
              ml: 2,
              fontSize: 13,
              textTransform: "none",
              px: 1.5,
              py: 0.5,
              backgroundColor: "#3B82F6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
              boxShadow: "none",
            }}
            onClick={() => {
              setEditData(null);
              setShowForm(true);
            }}
          >
            Add
          </Button>
        </Box>
      )}

      {showForm ? (
        <VehicleFormAdd
          editData={editData}
          onCancel={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            fetchRows();
          }}
        />
      ) : (
        <>
          <Paper
            elevation={2}
            sx={{ width: "100%", overflowX: "auto", borderRadius: 2 }}
          >
            <Table size="small" sx={{ minWidth: 1000, ...tableStyles }}>
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle Number</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>AC Type</TableCell>
                  <TableCell>Fuel Type</TableCell>
                  <TableCell>Ownership</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        No vehicles to display.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <TableRow key={row.vehicle_id} hover>
                    <TableCell>{row.vehicle_number}</TableCell>
                    <TableCell>{row.vehicle_group}</TableCell>
                    <TableCell>{row.vehicle_type_name}</TableCell>
                    <TableCell>{row.vehicle_ac_type}</TableCell>
                    <TableCell>{row.vehicle_fuel_type}</TableCell>
                    <TableCell>{row.vehicle_ownership}</TableCell>
                    <TableCell>{row.vehicle_location}</TableCell>
                    <TableCell>{row.active}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleRowMenuOpen(e, row)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={-1} // Unknown count, API should eventually provide total count for better pagination
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
              labelRowsPerPage="Rows per page"
              labelDisplayedRows={({ from, to }) => `Showing ${from}-${to}`}
              sx={{
                ".MuiTablePagination-toolbar": { minHeight: 38, height: 38 },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                  { fontSize: 12 },
              }}
            />
          </Paper>

          {/* Row Actions: Edit/Delete menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleRowMenuClose}
            PaperProps={{ sx: { minWidth: 120 } }}
          >
            <MenuItem
              onClick={() => {
                if (menuRow) handleEdit(menuRow);
              }}
              sx={{ fontSize: 13 }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>

            <MenuItem
              onClick={() => handleDelete(menuRow)}
              sx={{ fontSize: 13, color: "error.main" }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
